import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Status } from '../common/enums/status';
import { Connector } from '../pages/Station/types';
import { ocppService } from '../services/ocpp/OcppService';
import { sessionForStation } from '../utils/activeSessions';
import { SessionType } from '../components/common/Card/UserSessionCard/types';

interface IUseHookProps {
  setLoading: Dispatch<SetStateAction<boolean>>;
  connectorsStatus?: [] | Connector[];
  setChosenConnector?: Dispatch<SetStateAction<Connector | null>>;
  isUserPhoneConfirmed: boolean | null;
  stationId?: string;
}

export const useSessionStatus = ({
  setLoading,
  connectorsStatus,
  setChosenConnector,
  isUserPhoneConfirmed,
  stationId,
}: IUseHookProps) => {
  const [activeChargingSession, setActiveChargingSession] = useState<
    SessionType | undefined | null
  >(null);

  const updateSession = async () => {
    try {
      // Try getLastUserSession first
      const lastSession = await ocppService.getLastUserSession(stationId);

      if (lastSession?.chargingStatus === Status.CHARGING) {
        setActiveChargingSession(lastSession);
        if (setChosenConnector) {
          setTransactionInfo(lastSession);
        }
        setLoading(false);
        return;
      }

      // Fallback: the live charges the account has running. Scoped to this
      // station when the screen is about one, so a charge at another station
      // can't redirect the driver away from the station they are looking at.
      try {
        const sessions = await ocppService.getActiveSessions();
        const activeProgress = stationId
          ? sessionForStation(sessions, stationId)
          : sessions[0];
        if (activeProgress?.stationId && activeProgress?.connectorId) {
          // Build a minimal session object for redirect
          setActiveChargingSession({
            stationId: activeProgress.stationId,
            connectorId: activeProgress.connectorId,
            sessionId: Number(activeProgress.sessionId),
            chargingStatus: Status.CHARGING,
            meterValue: activeProgress.meterValue ?? 0,
            meterStart: activeProgress.meterStart ?? 0,
            meterStop: activeProgress.meterStop ?? 0,
          } as SessionType);
        }
      } catch {
        // No active session
      }

      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isUserPhoneConfirmed) return;

    updateSession();
  }, [connectorsStatus, isUserPhoneConfirmed]);

  if (!isUserPhoneConfirmed) return { activeChargingTransaction: null };

  const setTransactionInfo = (lastTransaction: SessionType) => {
    if (connectorsStatus) {
      connectorsStatus.forEach((connector) => {
        if (connector.connectorId === +lastTransaction.connectorId) {
          if (setChosenConnector) {
            setChosenConnector({
              connectorId: connector.connectorId,
              sessionId: lastTransaction.sessionId,
              status: connector.status,
            });
          }
        }
      });
    }
  };

  return { activeChargingSession };
};
